// Context for managing the facility console drawer and selected facility
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getFacilities } from '../services/api';
import { setActiveFacilityId, onFacilityChange } from '../services/appBootstrap';

const FacilityConsoleContext = createContext();

export const FacilityConsoleProvider = ({ children }) => {
  const { isAuthenticated, roles } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('activeFacilityId') || null;
  });
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Check if user has facility access
  const hasFacilityAccess = useMemo(() => {
    return roles.some(role => ["Owner", "Manager", "Staff"].includes(role.role));
  }, [roles]);

  // Get role for selected facility
  const selectedFacilityRole = useMemo(() => {
    if (!selectedFacilityId) return null;
    const match = roles.find(r => r.facility === selectedFacilityId);
    return match?.role || null;
  }, [roles, selectedFacilityId]);

  // Fetch facilities when user is authenticated and has access
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!isAuthenticated || !hasFacilityAccess) {
        setLoading(false);
        return;
      }

      try {
        const response = await getFacilities();
        const facilityList = response || [];
        setFacilities(facilityList);

        // Prod 2026-09-30: a saved id that is NOT one of this user's
        // facilities (a stale localStorage value — prod's one facility is
        // 73ff…, the browser held 71423435…, which names nothing) built every
        // /my-store/<id> link from a facility that does not exist, and every
        // request on those pages was refused as "no role". Replace it with
        // a real one, exactly as an empty value is replaced below.
        const savedIsMine = facilityList.some(
          (f) => String(f.id) === String(selectedFacilityId)
        );
        if (facilityList.length > 0 && selectedFacilityId && !savedIsMine) {
          const facilityId = facilityList[0].id;
          console.warn('[FacilityConsole] saved facility is not one of yours — using', facilityId);
          setSelectedFacilityId(facilityId);
          setActiveFacilityId(facilityId);
        } else if (facilityList.length === 1 && !selectedFacilityId) {
          // Auto-select facility if only one exists and none is currently selected
          const facilityId = facilityList[0].id;
          setSelectedFacilityId(facilityId);
          localStorage.setItem('activeFacilityId', facilityId);
          console.debug('[FacilityConsoleDrawer] defaulted to facility[0]', facilityId);
        } else if (facilityList.length > 1 && !selectedFacilityId) {
          // Default to first facility if multiple exist and none selected
          const facilityId = facilityList[0].id;
          setSelectedFacilityId(facilityId);
          localStorage.setItem('activeFacilityId', facilityId);
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
        setFacilities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
    // Remove selectedFacilityId from deps to prevent re-fetching when facility changes
  }, [isAuthenticated, hasFacilityAccess]);

  // Handle facility switch
  const switchFacility = (facilityId) => {
    if (facilityId !== selectedFacilityId) {
      console.debug('[FacilityContext] set', facilityId, {
        previous: selectedFacilityId,
        facility: facilities.find(f => f.id === facilityId)?.facility_name
      });
      setSelectedFacilityId(facilityId);
      // Sync with bootstrap module (handles localStorage and notifications)
      setActiveFacilityId(facilityId);
    }
  };

  // Subscribe to facility changes from bootstrap module
  useEffect(() => {
    const unsubscribe = onFacilityChange((facilityId) => {
      if (facilityId !== selectedFacilityId) {
        console.debug('[FacilityConsoleContext] External facility change:', facilityId);
        setSelectedFacilityId(facilityId);
      }
    });
    return unsubscribe;
  }, [selectedFacilityId]);

  // Toggle drawer
  const toggleDrawer = () => {
    const newState = !drawerOpen;
    if (newState) {
      console.debug('[FacilityConsoleDrawer] open', { 
        userId: roles[0]?.user_id, 
        facilitiesCount: facilities.length 
      });
    }
    setDrawerOpen(newState);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const openDrawer = () => {
    console.debug('[FacilityConsoleDrawer] open', { 
      userId: roles[0]?.user_id, 
      facilitiesCount: facilities.length 
    });
    setDrawerOpen(true);
  };

  const value = {
    facilities,
    selectedFacilityId,
    selectedFacilityRole,
    hasFacilityAccess,
    loading,
    drawerOpen,
    switchFacility,
    toggleDrawer,
    closeDrawer,
    openDrawer,
    isOwnerOrManager: selectedFacilityRole === 'Owner' || selectedFacilityRole === 'Manager',
    isOwner: selectedFacilityRole === 'Owner',
    isManager: selectedFacilityRole === 'Manager',
  };

  return (
    <FacilityConsoleContext.Provider value={value}>
      {children}
    </FacilityConsoleContext.Provider>
  );
};

export const useFacilityConsole = () => {
  const context = useContext(FacilityConsoleContext);
  if (!context) {
    throw new Error('useFacilityConsole must be used within FacilityConsoleProvider');
  }
  return context;
};

export default FacilityConsoleProvider;